import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

interface InternalLink {
  title: string;
  description: string;
  url: string;
  keywords?: string[];
}

interface InternalLinksProps {
  links: InternalLink[];
  title?: string;
  language?: 'es' | 'en';
}

export const InternalLinks = ({ links, title, language = 'es' }: InternalLinksProps) => {
  const defaultTitle = language === 'es' ? 'Contenido Relacionado' : 'Related Content';
  
  return (
    <section className="py-12 border-t border-border" aria-label={defaultTitle}>
      <div className="container-custom">
        <h2 className="text-2xl font-bold mb-6 text-foreground">
          {title || defaultTitle}
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {links.map((link, index) => (
            <Link 
              key={index}
              to={link.url}
              className="group"
            >
              <Card className="h-full hover:shadow-lg transition-all duration-300 hover:scale-[1.02] border-border/50 hover:border-primary/30">
                <CardContent className="p-6 space-y-3">
                  <h3 className="font-semibold text-lg group-hover:text-primary transition-colors flex items-center justify-between">
                    {link.title}
                    <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {link.description}
                  </p>
                  {link.keywords && link.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {link.keywords.slice(0, 3).map((keyword, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Predefined related links for common pages
export const seoRelatedLinks = (language: 'es' | 'en'): InternalLink[] => 
  language === 'es' ? [
    {
      title: "Auditoría SEO Profesional",
      description: "Análisis completo de tu sitio web para identificar oportunidades de mejora y problemas técnicos.",
      url: "/servicios/seo-diagnostico",
      keywords: ["auditoría SEO", "análisis web", "SEO técnico"]
    },
    {
      title: "Posicionamiento en LLMs",
      description: "Optimiza tu contenido para aparecer en ChatGPT, Claude y otras inteligencias artificiales.",
      url: "/servicios/posicionamiento-llm",
      keywords: ["LLM", "ChatGPT", "AI search"]
    },
    {
      title: "Google Ads Houston",
      description: "Campañas de publicidad en Google optimizadas para el mercado hispano con medición orientada a negocio.",
      url: "/google-ads-houston",
      keywords: ["Google Ads", "PPC", "SEM"]
    }
  ] : [
    {
      title: "Professional SEO Audit",
      description: "Complete website analysis to identify improvement opportunities and technical issues.",
      url: "/en/services/seo-audit",
      keywords: ["SEO audit", "website analysis", "technical SEO"]
    },
    {
      title: "LLM Positioning",
      description: "Optimize your content to appear in ChatGPT, Claude and other AI search results.",
      url: "/en/services/llm-positioning",
      keywords: ["LLM", "ChatGPT", "AI search"]
    },
    {
      title: "Google Ads Management",
      description: "Paid search built around real commercial intent, with spend control and clear measurement.",
      url: "/en/services/google-ads",
      keywords: ["Google Ads", "PPC", "SEM"]
    }
  ];
